import React, { createContext, useContext, useState, useEffect } from "react";
import chroma from "chroma-js";
import { useAuth } from "./AuthContext"; // Import useAuth hook

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};

// Enhanced color generation using chroma.js
const generateAdvancedColorScheme = (primaryColor, isDarkMode = false) => {
  try {
    const primary = chroma(primaryColor);

    // Generate a comprehensive color palette
    const palette = {
      primary: primary.hex(),
      secondary:
        primary.luminance() > 0.5
          ? primary.darken(0.1).hex()
          : primary.brighten(0.1).hex(),
      tertiary:
        primary.luminance() > 0.5
          ? primary.darken(0.2).hex()
          : primary.brighten(0.2).hex(),
      accent: primary.set("hsl.h", primary.get("hsl.h") + 180).hex(),
      success: chroma("#10b981").hex(),
      warning: chroma("#f59e0b").hex(),
      danger: chroma("#ef4444").hex(),
      info: chroma("#3b82f6").hex(),
    };

    if (isDarkMode) {
      return {
        // Dark backgrounds
        primary: "#0f1419",
        secondary: "#1a1f25",
        tertiary: "#232a31",

        // Fixed text colors for dark mode - always use light text
        textPrimary: "#e6e9ed",
        textSecondary: "#b8bfc7",
        textMuted: "#8b929a",

        // Borders
        borderPrimary: "#3a424a",
        borderSecondary: "#2d3339",

        // Theme colors
        active: palette.primary,
        accent: chroma(palette.accent).brighten(0.5).hex(),
        highlight: primary.brighten(0.3).hex(),

        // Status colors adjusted for dark mode
        success: chroma(palette.success).brighten(0.2).hex(),
        warning: chroma(palette.warning).brighten(0.2).hex(),
        danger: chroma(palette.danger).brighten(0.2).hex(),
        info: chroma(palette.info).brighten(0.2).hex(),
        muted: "#374151",
      };
    }

    return {
      // Light backgrounds
      primary: "#ffffff",
      secondary: "#f8fafc",
      tertiary: "#f1f5f9",

      // Fixed text colors for light mode - always use dark text
      textPrimary: "#1e293b",
      textSecondary: "#64748b",
      textMuted: "#94a3b8",

      // Borders
      borderPrimary: "#e2e8f0",
      borderSecondary: "#f1f5f9",

      // Theme colors
      active: palette.primary,
      accent: palette.accent,
      highlight: primary.brighten(0.1).hex(),

      // Status colors
      success: palette.success,
      warning: palette.warning,
      danger: palette.danger,
      info: palette.info,
      muted: "#f1f5f9",
    };
  } catch (error) {
    console.error("Error generating color scheme:", error);
    return getDefaultColors(isDarkMode);
  }
};

// Generate monochromatic scheme using chroma.js
const generateMonochromaticScheme = (primaryColor, isDarkMode = false) => {
  try {
    const primary = chroma(primaryColor);
    const hue = primary.get("hsl.h");
    const saturation = primary.get("hsl.s");

    // Create a monochromatic scale
    const scale = chroma
      .scale([
        chroma.hsl(hue, saturation, 0.95),
        chroma.hsl(hue, saturation, 0.05),
      ])
      .mode("hsl")
      .colors(10);

    const baseScheme = generateAdvancedColorScheme(primaryColor, isDarkMode);

    return {
      ...baseScheme,
      secondary: scale[1],
      tertiary: scale[2],
      accent: scale[7],
      highlight: scale[3],
      muted: scale[1],
    };
  } catch (error) {
    console.error("Error generating monochromatic scheme:", error);
    return generateAdvancedColorScheme(primaryColor, isDarkMode);
  }
};

// Generate complementary scheme using chroma.js
const generateComplementaryScheme = (primaryColor, isDarkMode = false) => {
  try {
    const primary = chroma(primaryColor);
    const complement = primary.set("hsl.h", primary.get("hsl.h") + 180);

    const baseScheme = generateAdvancedColorScheme(primaryColor, isDarkMode);

    return {
      ...baseScheme,
      accent: complement.hex(),
      highlight: complement.brighten(0.2).hex(),
      secondary: isDarkMode
        ? complement.darken(2).hex()
        : complement.brighten(2).hex(),
    };
  } catch (error) {
    console.error("Error generating complementary scheme:", error);
    return generateAdvancedColorScheme(primaryColor, isDarkMode);
  }
};

// Generate triadic scheme using chroma.js
const generateTriadicScheme = (primaryColor, isDarkMode = false) => {
  try {
    const primary = chroma(primaryColor);
    const triad1 = primary.set("hsl.h", primary.get("hsl.h") + 120);
    const triad2 = primary.set("hsl.h", primary.get("hsl.h") + 240);

    const baseScheme = generateAdvancedColorScheme(primaryColor, isDarkMode);

    return {
      ...baseScheme,
      accent: triad1.hex(),
      highlight: triad2.hex(),
      secondary: isDarkMode ? triad1.darken(2).hex() : triad1.brighten(2).hex(),
      tertiary: isDarkMode ? triad2.darken(2).hex() : triad2.brighten(2).hex(),
    };
  } catch (error) {
    console.error("Error generating triadic scheme:", error);
    return generateAdvancedColorScheme(primaryColor, isDarkMode);
  }
};

// Generate analogous scheme using chroma.js
const generateAnalogousScheme = (primaryColor, isDarkMode = false) => {
  try {
    const primary = chroma(primaryColor);
    const analog1 = primary.set("hsl.h", primary.get("hsl.h") + 30);
    const analog2 = primary.set("hsl.h", primary.get("hsl.h") - 30);

    const baseScheme = generateAdvancedColorScheme(primaryColor, isDarkMode);

    return {
      ...baseScheme,
      accent: analog1.hex(),
      highlight: analog2.hex(),
      secondary: isDarkMode
        ? analog1.darken(1.5).hex()
        : analog1.brighten(1.5).hex(),
      tertiary: isDarkMode
        ? analog2.darken(1.5).hex()
        : analog2.brighten(1.5).hex(),
    };
  } catch (error) {
    console.error("Error generating analogous scheme:", error);
    return generateAdvancedColorScheme(primaryColor, isDarkMode);
  }
};

// Generate gradient-based scheme
const generateGradientScheme = (primaryColor, isDarkMode = false) => {
  try {
    const primary = chroma(primaryColor);

    // Create a gradient from primary to a harmonious color
    const endColor = primary.set("hsl.h", primary.get("hsl.h") + 60);
    const gradient = chroma.scale([primary, endColor]).mode("lch").colors(5);

    const baseScheme = generateAdvancedColorScheme(primaryColor, isDarkMode);

    return {
      ...baseScheme,
      secondary: gradient[1],
      tertiary: gradient[2],
      accent: gradient[3],
      highlight: gradient[4],
    };
  } catch (error) {
    console.error("Error generating gradient scheme:", error);
    return generateAdvancedColorScheme(primaryColor, isDarkMode);
  }
};

// Default colors fallback
const getDefaultColors = (isDarkMode) => ({
  primary: isDarkMode ? "#0f1419" : "#ffffff",
  secondary: isDarkMode ? "#1a1f25" : "#f8fafc",
  tertiary: isDarkMode ? "#232a31" : "#f1f5f9",
  textPrimary: isDarkMode ? "#e6e9ed" : "#1e293b",
  textSecondary: isDarkMode ? "#b8bfc7" : "#64748b",
  textMuted: isDarkMode ? "#8b929a" : "#94a3b8",
  borderPrimary: isDarkMode ? "#3a424a" : "#e2e8f0",
  borderSecondary: isDarkMode ? "#2d3339" : "#f1f5f9",
  active: "#3b82f6",
  accent: "#60a5fa",
  highlight: "#fbbf24",
  success: "#4ade80",
  warning: "#fbbf24",
  danger: "#f87171",
  info: "#60a5fa",
  muted: isDarkMode ? "#374151" : "#f1f5f9",
});

// Enhanced contrast checking with chroma.js
const getContrastInfo = (backgroundColor, textColor) => {
  try {
    if (!backgroundColor || !textColor) {
      return { ratio: "1.00", isAccessible: false, level: "Fail" };
    }

    const ratio = chroma.contrast(backgroundColor, textColor);
    return {
      ratio: ratio.toFixed(2),
      isAccessible: ratio >= 4.5,
      level: ratio >= 7 ? "AAA" : ratio >= 4.5 ? "AA" : "Fail",
    };
  } catch (error) {
    console.error("Error calculating contrast:", error);
    return { ratio: "1.00", isAccessible: false, level: "Fail" };
  }
};

// Color utility functions using chroma.js
const colorUtils = {
  // Check if color is light
  isLight: (color) => {
    try {
      return chroma(color).luminance() > 0.5;
    } catch {
      return false;
    }
  },

  // Get best text color for background
  getBestTextColor: (
    bgColor,
    lightColor = "#ffffff",
    darkColor = "#000000"
  ) => {
    try {
      const lightContrast = chroma.contrast(bgColor, lightColor);
      const darkContrast = chroma.contrast(bgColor, darkColor);
      return lightContrast > darkContrast ? lightColor : darkColor;
    } catch {
      return darkColor;
    }
  },

  // Darken color
  darken: (color, amount = 0.1) => {
    try {
      return chroma(color).darken(amount).hex();
    } catch {
      return color;
    }
  },

  // Brighten color
  brighten: (color, amount = 0.1) => {
    try {
      return chroma(color).brighten(amount).hex();
    } catch {
      return color;
    }
  },

  // Saturate color
  saturate: (color, amount = 0.1) => {
    try {
      return chroma(color).saturate(amount).hex();
    } catch {
      return color;
    }
  },

  // Desaturate color
  desaturate: (color, amount = 0.1) => {
    try {
      return chroma(color).desaturate(amount).hex();
    } catch {
      return color;
    }
  },

  // Mix colors
  mix: (color1, color2, ratio = 0.5) => {
    try {
      return chroma.mix(color1, color2, ratio).hex();
    } catch {
      return color1;
    }
  },

  // Generate color scale
  scale: (colors, steps = 10) => {
    try {
      return chroma.scale(colors).mode("lch").colors(steps);
    } catch {
      return [colors[0] || "#000000"];
    }
  },

  // Generate opacity variants
  generateOpacityVariants: (color, opacities = [0.2, 0.4, 0.6, 0.8]) => {
    try {
      const variants = {};
      opacities.forEach((opacity) => {
        const percent = Math.round(opacity * 100);
        variants[`${percent}`] = chroma(color).alpha(opacity).css();
      });
      return variants;
    } catch {
      return {};
    }
  },
};

// Enhanced function to auto-adjust text colors based on contrast
const getOptimalTextColors = (colors, isDarkMode) => {
  try {
    const textColors = {};

    // Define fallback colors
    const fallbackLight = isDarkMode ? "#ffffff" : "#000000";
    const fallbackDark = isDarkMode ? "#000000" : "#ffffff";

    // Calculate optimal text colors for each background
    Object.entries(colors).forEach(([key, bgColor]) => {
      if (key.includes("text") || !bgColor) return;

      try {
        // Test both light and dark text options
        const lightTextContrast = chroma.contrast(bgColor, "#ffffff");
        const darkTextContrast = chroma.contrast(bgColor, "#000000");

        // Choose the text color with better contrast
        const optimalTextColor =
          lightTextContrast > darkTextContrast ? "#ffffff" : "#000000";

        // Ensure minimum contrast ratio of 4.5
        if (Math.max(lightTextContrast, darkTextContrast) >= 4.5) {
          textColors[`${key}Text`] = optimalTextColor;
        } else {
          // If neither meets the standard, use fallback
          textColors[`${key}Text`] = fallbackLight;
        }
      } catch (error) {
        textColors[`${key}Text`] = fallbackLight;
      }
    });

    return textColors;
  } catch (error) {
    console.error("Error calculating optimal text colors:", error);
    return {};
  }
};

// Export the enhanced theme provider with all the new functionality
export const ThemeProvider = ({ children }) => {
  const { user } = useAuth(); // Get user data from AuthContext

  const [isDarkMode, setIsDarkMode] = useState(() => {
    try {
      const saved = localStorage.getItem("darkMode");
      return saved ? JSON.parse(saved) : false;
    } catch (error) {
      console.error("Error reading darkMode from storage:", error);
      return false;
    }
  });

  // Safe getter from storage (both localStorage and sessionStorage)
  const safeGetFromStorage = (key, defaultValue = null) => {
    try {
      // Try sessionStorage first
      let value = sessionStorage.getItem(key);

      // If not found in sessionStorage, try localStorage
      if (value === null) {
        value = localStorage.getItem(key);
      }

      if (value === null) return defaultValue;

      // Check if the value looks like a hex color (starts with #)
      if (typeof value === "string" && value.startsWith("#")) {
        return value;
      }

      // Check if the value looks like a boolean
      if (value === "true" || value === "false") {
        return value === "true";
      }

      // Try to parse as JSON for objects/arrays
      try {
        return JSON.parse(value);
      } catch (parseError) {
        // If JSON parsing fails, return the raw string value
        return value;
      }
    } catch (error) {
      console.error(`Error reading ${key} from storage:`, error);
      return defaultValue;
    }
  };

  // Initialize primaryColor with user's colorTheme or fallback
  const [primaryColor, setPrimaryColor] = useState(() => {
    // Priority: user.colorTheme > sessionStorage > default
    if (user?.colorTheme) {
      return user.colorTheme;
    }
    return safeGetFromStorage("colorTheme", "#3b82f6");
  });

  const [colorSchemeType, setColorSchemeType] = useState(() =>
    safeGetFromStorage("colorSchemeType", "advanced")
  );

  // Enhanced color scheme generator
  const generateColorScheme = (
    primaryColor,
    schemeType,
    isDarkMode = false
  ) => {
    try {
      switch (schemeType) {
        case "monochromatic":
          return generateMonochromaticScheme(primaryColor, isDarkMode);
        case "complementary":
          return generateComplementaryScheme(primaryColor, isDarkMode);
        case "triadic":
          return generateTriadicScheme(primaryColor, isDarkMode);
        case "analogous":
          return generateAnalogousScheme(primaryColor, isDarkMode);
        case "gradient":
          return generateGradientScheme(primaryColor, isDarkMode);
        default:
          return generateAdvancedColorScheme(primaryColor, isDarkMode);
      }
    } catch (error) {
      console.error("Error in generateColorScheme:", error);
      return getDefaultColors(isDarkMode);
    }
  };

  const [customColors, setCustomColors] = useState(() =>
    generateColorScheme(primaryColor, colorSchemeType, isDarkMode)
  );

  const [isColorPickerOpen, setIsColorPickerOpen] = useState(false);

  // Watch for user changes and update primaryColor when user.colorTheme changes
  useEffect(() => {
    if (user?.colorTheme && user.colorTheme !== primaryColor) {
      console.log("Updating primary color from user data:", user.colorTheme);
      setPrimaryColor(user.colorTheme);

      // Also update sessionStorage for consistency
      safeSetToStorage("colorTheme", user.colorTheme);

      // Dispatch custom event to notify other parts of the app
      window.dispatchEvent(
        new CustomEvent("themeColorUpdate", {
          detail: { colorTheme: user.colorTheme },
        })
      );
    }
  }, [user?.colorTheme, primaryColor]);

  // Enhanced apply theme colors function with auto text colors and opacity variants
  const applyThemeColors = (colors) => {
    try {
      if (!colors || typeof colors !== "object") {
        console.warn("Invalid colors object:", colors);
        return;
      }

      const root = document.documentElement;
      if (!root) {
        console.warn("Document root not available");
        return;
      }

      // Set data-theme attribute for CSS
      root.setAttribute("data-theme", isDarkMode ? "dark" : "light");

      // Generate optimal text colors based on contrast
      const optimalTextColors = getOptimalTextColors(colors, isDarkMode);

      // Generate opacity variants for the active color
      const activeOpacityVariants = colorUtils.generateOpacityVariants(
        colors.active || primaryColor,
        [0.2, 0.4, 0.6, 0.8]
      );

      // Apply all color variables with safety checks
      Object.entries(colors).forEach(([key, value]) => {
        if (typeof value === "string") {
          const cssVar = `--${key
            .replace(/([A-Z])/g, "-$1")
            .toLowerCase()}-color`;
          root.style.setProperty(cssVar, value);
        }
      });

      // Apply optimal text colors
      Object.entries(optimalTextColors).forEach(([key, value]) => {
        const cssVar = `--${key
          .replace(/([A-Z])/g, "-$1")
          .toLowerCase()}-color`;
        root.style.setProperty(cssVar, value);
      });

      // Apply active color opacity variants
      Object.entries(activeOpacityVariants).forEach(([opacity, value]) => {
        root.style.setProperty(`--active-${opacity}-color`, value);
      });

      // Apply legacy variables for backward compatibility
      const legacyVars = {
        "--primary-color": colors.primary,
        "--bg-primary": colors.primary,
        "--bg-secondary": colors.secondary,
        "--bg-tertiary": colors.tertiary,
        "--active-color": colors.active,
        "--text-primary": colors.textPrimary,
        "--text-secondary": colors.textSecondary,
        "--text-muted": colors.textMuted,
        "--border-primary": colors.borderPrimary,
        "--border-secondary": colors.borderSecondary,
        "--success-color": colors.success,
        "--warning-color": colors.warning,
        "--danger-color": colors.danger,
        "--info-color": colors.info,

        // Auto-generated text colors
        "--primary-text-color":
          optimalTextColors.primaryText || colors.textPrimary,
        "--secondary-text-color":
          optimalTextColors.secondaryText || colors.textSecondary,
        "--active-text-color":
          optimalTextColors.activeText ||
          colorUtils.getBestTextColor(colors.active),
        "--accent-text-color":
          optimalTextColors.accentText ||
          colorUtils.getBestTextColor(colors.accent),

        // Active color opacity variants
        "--active-20-color":
          activeOpacityVariants["20"] || chroma(colors.active).alpha(0.2).css(),
        "--active-40-color":
          activeOpacityVariants["40"] || chroma(colors.active).alpha(0.4).css(),
        "--active-60-color":
          activeOpacityVariants["60"] || chroma(colors.active).alpha(0.6).css(),
        "--active-80-color":
          activeOpacityVariants["80"] || chroma(colors.active).alpha(0.8).css(),
      };

      Object.entries(legacyVars).forEach(([property, value]) => {
        if (value) {
          root.style.setProperty(property, value);
        }
      });
    } catch (error) {
      console.error("Error applying theme colors:", error);
    }
  };

  // Toggle dark mode with error handling
  const toggleTheme = () => {
    try {
      setIsDarkMode((prev) => !prev);
    } catch (error) {
      console.error("Error toggling theme:", error);
    }
  };

  // Update colors when theme settings change
  useEffect(() => {
    try {
      const newColorScheme = generateColorScheme(
        primaryColor,
        colorSchemeType,
        isDarkMode
      );

      const updatedColors = {
        ...customColors,
        ...newColorScheme,
        active: primaryColor,
      };

      setCustomColors(updatedColors);
      applyThemeColors(updatedColors);

      // Save to localStorage with error handling
      safeSetToStorage("darkMode", isDarkMode, true);
      safeSetToStorage("colorTheme", primaryColor);
      safeSetToStorage("colorSchemeType", colorSchemeType);
      safeSetToStorage("customColors", updatedColors);
    } catch (error) {
      console.error("Error updating theme colors:", error);
    }
  }, [primaryColor, colorSchemeType, isDarkMode]);

  // Listen for custom theme update events
  useEffect(() => {
    const handleThemeColorUpdate = (event) => {
      try {
        const { colorTheme } = event.detail || {};
        if (colorTheme && colorTheme !== primaryColor) {
          setPrimaryColor(colorTheme);
        }
      } catch (error) {
        console.error("Error handling theme color update:", error);
      }
    };

    window.addEventListener("themeColorUpdate", handleThemeColorUpdate);
    return () => {
      window.removeEventListener("themeColorUpdate", handleThemeColorUpdate);
    };
  }, [primaryColor]);

  // Periodically check for colorTheme updates
  useEffect(() => {
    const checkForColorThemeUpdates = () => {
      try {
        const currentColorTheme = safeGetFromStorage("colorTheme", null);
        if (currentColorTheme && currentColorTheme !== primaryColor) {
          setPrimaryColor(currentColorTheme);
        }
      } catch (error) {
        console.error("Error checking for color theme updates:", error);
      }
    };

    checkForColorThemeUpdates();
    const intervalId = setInterval(checkForColorThemeUpdates, 500);
    return () => clearInterval(intervalId);
  }, [primaryColor]);

  // Listen for storage changes (cross-tab synchronization)
  useEffect(() => {
    const handleStorageChange = (e) => {
      try {
        if (e.key === "darkMode" && e.newValue) {
          setIsDarkMode(JSON.parse(e.newValue));
        }
        if (e.key === "colorTheme" && e.newValue) {
          setPrimaryColor(e.newValue);
        }
        if (e.key === "colorSchemeType" && e.newValue) {
          setColorSchemeType(e.newValue);
        }
        if (e.key === "customColors" && e.newValue) {
          const parsedColors = JSON.parse(e.newValue);
          if (parsedColors && typeof parsedColors === "object") {
            setCustomColors(parsedColors);
          }
        }
      } catch (error) {
        console.error("Error handling storage change:", error);
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  // Check for sessionStorage changes on focus
  useEffect(() => {
    const handleFocus = () => {
      try {
        const savedColorTheme = safeGetFromStorage("colorTheme", null);
        if (savedColorTheme && savedColorTheme !== primaryColor) {
          setPrimaryColor(savedColorTheme);
        }

        const savedCustomColors = safeGetFromStorage("customColors", null);
        if (savedCustomColors && typeof savedCustomColors === "object") {
          if (
            JSON.stringify(savedCustomColors) !== JSON.stringify(customColors)
          ) {
            setCustomColors(savedCustomColors);
          }
        }
      } catch (error) {
        console.error("Error checking sessionStorage on focus:", error);
      }
    };

    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [customColors, primaryColor]);

  // Initial color application
  useEffect(() => {
    applyThemeColors(customColors);
  }, []);

  // Safe update functions
  const updatePrimaryColor = (newColor) => {
    try {
      if (newColor && typeof newColor === "string") {
        setPrimaryColor(newColor);
      }
    } catch (error) {
      console.error("Error updating primary color:", error);
    }
  };

  const updateColorSchemeType = (newType) => {
    try {
      if (newType && typeof newType === "string") {
        setColorSchemeType(newType);
      }
    } catch (error) {
      console.error("Error updating color scheme type:", error);
    }
  };

  // Safe setter to storage (with localStorage fallback for dark mode)
  const safeSetToStorage = (key, value, useLocalStorage = false) => {
    try {
      // Always stringify to ensure consistency
      const stringValue =
        typeof value === "string" ? value : JSON.stringify(value);

      if (useLocalStorage || key === "darkMode") {
        localStorage.setItem(key, stringValue);
      } else {
        sessionStorage.setItem(key, stringValue);
      }
    } catch (error) {
      console.error(`Error saving ${key} to storage:`, error);
    }
  };

  const updateColor = (colorKey, newColor) => {
    try {
      if (!colorKey || !newColor) return;

      setCustomColors((prev) => {
        const updated = {
          ...prev,
          [colorKey]: newColor,
        };

        if (colorKey === "active") {
          setPrimaryColor(newColor);
          safeSetToStorage("colorTheme", newColor);
        }

        safeSetToStorage("customColors", updated);
        applyThemeColors(updated);

        return updated;
      });
    } catch (error) {
      console.error("Error updating color:", error);
    }
  };

  const resetColors = () => {
    try {
      const defaultScheme = generateColorScheme(
        primaryColor,
        colorSchemeType,
        isDarkMode
      );
      setCustomColors(defaultScheme);
    } catch (error) {
      console.error("Error resetting colors:", error);
    }
  };

  const exportTheme = () => {
    try {
      const themeData = {
        isDarkMode,
        primaryColor,
        colorSchemeType,
        customColors,
        exportDate: new Date().toISOString(),
      };

      const blob = new Blob([JSON.stringify(themeData, null, 2)], {
        type: "application/json",
      });

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `theme-${
        isDarkMode ? "dark" : "light"
      }-${primaryColor.replace("#", "")}-${Date.now()}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error exporting theme:", error);
    }
  };

  const importTheme = (file) => {
    try {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const themeData = JSON.parse(e.target.result);

          if (themeData.primaryColor) {
            setPrimaryColor(themeData.primaryColor);
          }
          if (
            themeData.customColors &&
            typeof themeData.customColors === "object"
          ) {
            const importedColors = {
              ...themeData.customColors,
              active: themeData.primaryColor || themeData.customColors.active,
            };
            setCustomColors(importedColors);
          }
          if (themeData.colorSchemeType) {
            setColorSchemeType(themeData.colorSchemeType);
          }
          if (typeof themeData.isDarkMode === "boolean") {
            setIsDarkMode(themeData.isDarkMode);
          }
        } catch (error) {
          console.error("Error importing theme:", error);
          alert("Invalid theme file format");
        }
      };
      reader.readAsText(file);
    } catch (error) {
      console.error("Error setting up theme import:", error);
    }
  };

  const toggleColorPicker = () => {
    try {
      setIsColorPickerOpen((prev) => !prev);
    } catch (error) {
      console.error("Error toggling color picker:", error);
    }
  };

  const contextValue = {
    isDarkMode,
    toggleTheme,
    primaryColor,
    updatePrimaryColor,
    colorSchemeType,
    updateColorSchemeType,
    customColors,
    updateColor,
    resetColors,
    exportTheme,
    importTheme,
    isColorPickerOpen,
    toggleColorPicker,
    currentThemeColors: customColors,
    getContrastInfo,
    generateColorScheme,
    colorUtils,
    availableSchemes: [
      "advanced",
      "monochromatic",
      "complementary",
      "triadic",
      "analogous",
      "gradient",
    ],
  };

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};
